import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { MessageCircle } from "lucide-react";
import { useWhatsAppConfig, useWhatsAppStatus } from "@/hooks/useWhatsAppContext";
import { cn } from "@/lib/utils";

const WhatsAppWidget = lazy(() => import("./WhatsAppWidget"));

export default function WhatsAppFab() {
  const config = useWhatsAppConfig();
  const status = useWhatsAppStatus(config);
  const [open, setOpen] = useState(false);
  const [showProactive, setShowProactive] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Proactive popup after 30s on page
  useEffect(() => {
    if (dismissed || open || !config.phone) return;
    const timer = setTimeout(() => setShowProactive(true), 30000);
    return () => clearTimeout(timer);
  }, [dismissed, open, config.phone]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setShowProactive(false);
    setDismissed(true);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  // Don't render if no phone configured
  if (!config.phone) return null;

  const statusColor = status === "online"
    ? "bg-[hsl(142,70%,45%)]"
    : status === "closing"
      ? "bg-[hsl(var(--warning))]"
      : "bg-muted-foreground/50";

  return (
    <>
      {/* Proactive nudge */}
      {showProactive && !open && (
        <div className="fixed bottom-[5.5rem] md:bottom-[4.5rem] right-4 sm:right-6 z-40 animate-in slide-in-from-bottom-4 fade-in duration-500">
          <div
            className="bg-card border border-border rounded-2xl shadow-xl p-3 max-w-[220px] cursor-pointer relative"
            onClick={handleOpen}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setShowProactive(false); setDismissed(true); }}
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[10px] hover:bg-destructive hover:text-destructive-foreground transition-colors"
              aria-label="Fechar"
            >
              ✕
            </button>
            <p className="text-xs text-foreground font-medium leading-snug">
              Precisa de ajuda? 😊
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Fale conosco pelo WhatsApp
            </p>
          </div>
          <div className="w-3 h-3 bg-card border-b border-r border-border rotate-45 absolute -bottom-1.5 right-7" />
        </div>
      )}

      {/* FAB Button */}
      <div className="fixed bottom-20 md:bottom-6 right-4 z-40 sm:right-6">
        <button
          className={cn(
            "relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-300 hover:scale-105 active:scale-95",
            open
              ? "bg-muted text-muted-foreground hover:bg-muted/80"
              : "bg-[hsl(142,70%,45%)] hover:bg-[hsl(142,70%,40%)] text-white hover:shadow-xl"
          )}
          onClick={open ? handleClose : handleOpen}
          aria-label={open ? "Fechar WhatsApp" : "Falar pelo WhatsApp"}
        >
          {open ? (
            <span className="text-lg font-bold">✕</span>
          ) : (
            <>
              <MessageCircle className="h-7 w-7" />
              {/* Status dot */}
              <span className={cn("absolute top-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white", statusColor)} />
            </>
          )}
        </button>
      </div>

      {/* Widget Panel */}
      {open && (
        <Suspense fallback={null}>
          <WhatsAppWidget config={config} status={status} onClose={handleClose} />
        </Suspense>
      )}
    </>
  );
}
